import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, scheduleProactiveRefresh } from '../services/api';
import { CompanyPicker } from './ui';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companySelectToken, setCompanySelectToken] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const navigate = useNavigate();

  // Check for session expiration message and OAuth company selection on mount.
  // Note: OAuth success is handled by /oauth/callback (OAuthCallback.js),
  // NOT by this page. MS redirects to /oauth/callback?oauth_success=true,
  // never to /login?oauth_success=true.
  useEffect(() => {
    const loginMessage = sessionStorage.getItem('login_message');
    const messageType = sessionStorage.getItem('login_message_type');

    if (loginMessage) {
      if (messageType === 'error') {
        setError(loginMessage);
      } else {
        setInfo(loginMessage);
      }

      // Clear the message from localStorage
      sessionStorage.removeItem('login_message');
      sessionStorage.removeItem('login_message_type');
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('company_select') === 'true') {
      // Handle OAuth company selection redirect (from OAuthCallback.js)
      const companyData = sessionStorage.getItem('oauth_company_select');
      if (companyData) {
        try {
          const { token, companies } = JSON.parse(companyData);
          sessionStorage.removeItem('oauth_company_select'); // Clear after reading
          setCompanySelectToken(token);
          setCompanies(companies);
          const primaryCompany = companies.find(c => c.is_primary);
          setSelectedCompany(primaryCompany ? primaryCompany.id : companies[0]?.id);
          setShowCompanySelector(true);
          // Clear URL parameter
          window.history.replaceState({}, document.title, '/login');
        } catch (err) {
          console.error('Failed to parse company selection data:', err);
          setError('Failed to load company selection data');
        }
      }
    } else if (urlParams.get('error')) {
      const errorMsg = urlParams.get('message') || 'OAuth login failed';
      setError(errorMsg);
    } else if (urlParams.get('verified') === 'true') {
      setInfo('Email verified successfully! You can now log in.');
    }

    // If the user navigated to /login directly (no ?next=/oauth/authorize),
    // clear any stale oauth_params from a previous SP-initiated flow.
    // This prevents a BC-initiated logout→re-auth chain from hijacking
    // a direct IDP login attempt.
    if (!urlParams.get('next')?.includes('/oauth/authorize')) {
      fetch('/api/auth/oauth/clear-pending', {
        method: 'POST',
        credentials: 'include'
      }).catch(() => {}); // Fire-and-forget — best-effort clear
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isDevelopment = process.env.NODE_ENV === 'development';
  const [debugInfo, setDebugInfo] = useState(null);

  // Handle company selection after login
  const handleCompanySelect = async (companyId) => {
    const targetCompanyId = companyId || selectedCompany;
    if (!targetCompanyId || !companySelectToken) {
      setError('Please select a company');
      return;
    }

    try {
      const response = await fetch('/api/auth/select-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${companySelectToken}`
        },
        body: JSON.stringify({ company_id: targetCompanyId })
      });

      const data = await response.json();

      if (data.status === 'success') {
        scheduleProactiveRefresh();

        setShowCompanySelector(false);
        
        // Check if this is a SAML login redirect
        const params = new URLSearchParams(window.location.search);
        const samlLogin = params.get('saml_login');
        const appId = params.get('app_id');
        const nextPath = params.get('next');

        if (samlLogin === 'true' && appId) {
          window.location.href = `/api/saml/sso/${appId}/continue`;
        } else if (nextPath) {
          window.location.href = nextPath;
        } else {
          navigate('/');
        }
      } else {
        setError(data.error || 'Failed to select company');
      }
    } catch (err) {
      console.error('Company selection error:', err);
      setError('Failed to select company');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await authAPI.login(formData.email, formData.password);

      // API schema requires status field
      if (!response.status) {
        setError('Invalid response from server');
        return;
      }

      if (response.status === 'error') {
        setError(response.error || 'Login failed');
        return;
      }

      // Handle MFA requirement
      if (response.mfa_required === true) {
        if (!response.mfa_token) {
          setError('MFA verification required but no token received');
          return;
        }

        sessionStorage.setItem('mfa_token', response.mfa_token);
        if (response.mfa_methods) {
          sessionStorage.setItem('mfa_methods', JSON.stringify(response.mfa_methods));
        }
        navigate('/mfa-verify');
        return;
      }

      // Handle MFA enrollment requirement (company/role policy requires MFA, user not enrolled)
      if (response.status === 'mfa_enrollment_required') {
        if (!response.mfa_token) {
          setError('MFA enrollment required but no token received');
          return;
        }
        sessionStorage.setItem('mfa_enrollment_token', response.mfa_token);
        if (response.message) sessionStorage.setItem('mfa_enrollment_message', response.message);
        if (response.mfa_policy) sessionStorage.setItem('mfa_enrollment_policy', response.mfa_policy);
        navigate('/mfa-setup');
        return;
      }

      // Handle company selection requirement (user belongs to multiple companies)
      if (response.status === 'company_select_required' || response.company_select_required === true) {
        if (!response.company_select_token || !response.companies) {
          setError('Company selection required but no data received');
          return;
        }

        setCompanySelectToken(response.company_select_token);
        setCompanies(response.companies);
        // Pre-select primary company if available
        const primaryCompany = response.companies.find(c => c.is_primary);
        setSelectedCompany(primaryCompany ? primaryCompany.id : response.companies[0]?.id);
        setShowCompanySelector(true);
        return;
      }

      // Handle pending approval — user has no company assigned
      if (response.status === 'pending_approval') {
        sessionStorage.setItem('pending_approval', JSON.stringify({
          user_email: response.user?.email,
          user_name: `${response.user?.firstname || ''} ${response.user?.lastname || ''}`.trim(),
          access_token: response.access_token,
        }));
        window.location.href = '/pending';
        return;
      }

      // Handle successful login — tokens are now HttpOnly cookies
      if (response.status === 'success') {
        scheduleProactiveRefresh();

        // Check if this is a SAML login redirect
        const params = new URLSearchParams(window.location.search);
        const samlLogin = params.get('saml_login');
        const appId = params.get('app_id');
        const nextPath = params.get('next');

        if (samlLogin === 'true' && appId) {
          // Redirect to SAML continue endpoint
          window.location.href = `/api/saml/sso/${appId}/continue`;
        } else if (nextPath) {
          // OAuth params are in Flask session — just redirect to authorize
          window.location.href = nextPath;
        } else {
          window.location.href = '/';
        }
      } else {
        setError('Login successful but no tokens received');
      }
    } catch (error) {
      console.error('Login error in component:', error);
      let errorMessage = 'Failed to login. ';
      
      if (error.message === 'Network Error') {
        errorMessage += 'Cannot connect to the server. Please check your connection and try again.';
        console.error('Network error details:', {
          serverUrl: window.location.origin + '/api',
          browserUrl: window.location.href
        });
      } else if (error.status === 401) {
        errorMessage += 'Invalid credentials.';
      } else if (error.status === 400) {
        errorMessage += error.message || 'Invalid input.';
      } else {
        errorMessage += error.message || 'An unexpected error occurred.';
      }
      
      setError(errorMessage);
      
      // Log error details
      logger.error('Login form submission failed', {
        timestamp: new Date().toISOString(),
        error: error.message,
        code: error.code,
        type: error.name,
        endpoint: window.location.origin + '/api/login'
      });

      // In development, show technical details
      if (isDevelopment) {
        setDebugInfo({
          timestamp: new Date().toISOString(),
          error: error.message,
          code: error.code,
          type: error.name,
          endpoint: window.location.origin + '/api/login',
          logs: logger.getLogs('error', 10) // Show last 10 error logs
        });
      }
    }
  };

  // Check if this is a SAML login
  const params = new URLSearchParams(window.location.search);
  const samlLogin = params.get('saml_login');
  const appId = params.get('app_id');

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, var(--color-primary, #4F46E5) 0%, var(--color-secondary, #7C3AED) 100%)'
        }}
      >
        {/* Decorative shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-white opacity-5 rounded-full translate-x-1/3 translate-y-1/3"></div>
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-white opacity-[0.03] rounded-full"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">TIMG Central</h1>
            <p className="mt-1 text-white/70 text-sm">Identity & Access Management</p>
          </div>

          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-sm">Secure Single Sign-On</h3>
                <p className="text-white/70 text-sm mt-0.5">One account for all your TIMG services</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-sm">Multi-Factor Authentication</h3>
                <p className="text-white/70 text-sm mt-0.5">Keep your account protected with MFA</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-sm">Unified App Access</h3>
                <p className="text-white/70 text-sm mt-0.5">Manage all your services from one dashboard</p>
              </div>
            </div>
          </div>

          <p className="text-white/50 text-xs">&copy; {new Date().getFullYear()} TIMG. All rights reserved.</p>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm">
          {/* Mobile-only logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-bold text-primary">TIMG Central</h1>
            <p className="text-gray-500 text-sm">Identity & Access Management</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
            <p className="mt-1 text-sm text-gray-500">Sign in to your account to continue</p>
          </div>

          {samlLogin === 'true' && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-primary text-center">
                <strong>SAML Login Required</strong><br />
                Please sign in to continue to your application
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-3 rounded-r-md">
              <p className="text-sm text-red-700">{error}</p>
              {isDevelopment && debugInfo && (
                <div className="mt-2 p-2 bg-gray-100 rounded text-left text-xs font-mono">
                  <div>Time: {debugInfo.timestamp}</div>
                  <div>Error: {debugInfo.error}</div>
                  <div>Code: {debugInfo.code}</div>
                  <div>Type: {debugInfo.type}</div>
                  <div>Endpoint: {debugInfo.endpoint}</div>
                </div>
              )}
            </div>
          )}

          {info && (
            <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-3 rounded-r-md">
              <p className="text-sm text-green-700">{info}</p>
            </div>
          )}

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm transition"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm transition"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
              />
              <div className="mt-2 text-right">
                <Link to="/reset-password" className="text-xs text-primary hover:text-primary font-medium">
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition"
            >
              Sign in
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-gray-50 text-gray-400 uppercase tracking-wide">or</span>
              </div>
            </div>

            {/* Microsoft OAuth Button */}
            <button
              type="button"
              onClick={() => {
                window.location.href = '/api/auth/oauth/login/microsoft';
              }}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-500">
            Don't have an account?{' '}
            <Link to="/signup" className="font-semibold text-primary hover:text-primary">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Company Selector Modal */}
      {showCompanySelector && (
        <CompanyPicker
          companies={companies}
          selectedId={selectedCompany}
          onSelect={handleCompanySelect}
          onCancel={() => {
            setShowCompanySelector(false);
            setCompanySelectToken(null);
            setCompanies([]);
            setError('');
          }}
          title="Select Company"
          submitLabel="Continue"
        />
      )}
    </div>
  );
};

export default LoginPage;